import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  cryptoPaymentAddressCreateCryptoPaymentAddressMutation,
  cryptoPaymentAddressListCryptoPaymentAddressQueryKey,
} from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldGroup,
  FieldLabel,
} from '@column/ui';

import {
  STABLECOIN_CHAIN_LABELS,
  SUPPORTED_STABLECOIN_CHAINS,
} from '~/app/pages/Transfers/TypeContent/stablecoin/constants';
import { NotificationList, RenderFields } from '~/components';
import { BankAccountSelector } from '~/lib/components/selectors';
import { useNotificationStore } from '~/stores/Notification';

import { ChainIcon } from './ChainIcon';

// Modal-scoped notification group: rendered inside DialogContent so errors sit in the modal's
// stacking context and stay above the overlay (the default `global` group renders behind it).
const MODAL_NOTIFICATION_DISPLAY = 'stablecoin-payment-address-modal';

interface FormValues {
  bankAccountId: string;
  chains: string[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  // When provided, the payment address is always funded by this bank account: the field is
  // preselected and the Bank Account selector is hidden. Used when the modal is opened from a
  // bank account page, where the account is already implied by context.
  bankAccountId?: string;
}

const defaultValues = (bankAccountId?: string): FormValues => ({
  bankAccountId: bankAccountId ?? '',
  chains: [...SUPPORTED_STABLECOIN_CHAINS],
});

export const CryptoPaymentAddressModal: FC<Props> = ({
  isOpen,
  onClose,
  onCreated,
  bankAccountId: fixedBankAccountId,
}) => {
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const hasModalNotifications = useNotificationStore((state) =>
    state.openNotifications.some((entry) => entry.display === MODAL_NOTIFICATION_DISPLAY)
  );
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({ defaultValues: defaultValues(fixedBankAccountId) });
  const { bankAccountId, chains } = form.watch();
  const isValid = !!bankAccountId && (chains?.length ?? 0) > 0;

  useEffect(() => {
    if (isOpen) {
      form.reset(defaultValues(fixedBankAccountId));
    }
  }, [isOpen, form, fixedBankAccountId]);

  const { mutateAsync, isPending } = useMutation(cryptoPaymentAddressCreateCryptoPaymentAddressMutation());

  const handleCreate = async () => {
    const values = form.getValues();
    try {
      const created = await mutateAsync({ body: { bank_account_id: values.bankAccountId, chains: values.chains } });
      await queryClient.invalidateQueries({
        queryKey: cryptoPaymentAddressListCryptoPaymentAddressQueryKey({ query: {} }),
      });
      if (created?.id) {
        onCreated(created.id);
      }
      onClose();
    } catch (error) {
      addDangerNotification({ content: (error as Error).message, display: MODAL_NOTIFICATION_DISPLAY });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="gap-3 sm:max-w-lg">
        <DialogHeader className="gap-1 text-left">
          <DialogTitle>Create Payment Address</DialogTitle>
          <DialogDescription>
            {fixedBankAccountId
              ? 'Create a payment address funded by this bank account.'
              : 'Create a payment address funded by one of your bank accounts.'}
          </DialogDescription>
        </DialogHeader>

        {/* Rendered only when this modal has an active error, inside DialogContent to stay above the backdrop. */}
        {hasModalNotifications && <NotificationList display={MODAL_NOTIFICATION_DISPLAY} />}

        <FormProvider {...form}>
          <div className="-mx-6 -mt-3 [--page-margin:24px]">
            {/* Full-bleed rule across the top of the form; an <hr> (not a <div>) keeps the first
                Section `:first-of-type` so its own top border stays suppressed (no doubled line). */}
            <hr className="mt-5 h-px w-full border-0 bg-border" />
            {RenderFields<FormValues>({
              sections: [
                {
                  fields: [
                    {
                      id: 'bankAccountId',
                      label: 'Bank Account',
                      oneColumn: true,
                      // Hidden when the bank account is fixed by context (e.g. opened from a bank
                      // account page); the value is still seeded into the form defaults.
                      hide: Boolean(fixedBankAccountId),
                      rules: { required: true },
                      description: 'The bank account whose USD balance funds transfers from this payment address.',
                      children: ({ value, onChange, onBlur }, { error, isTouched }) => (
                        <BankAccountSelector
                          value={value as string}
                          onChange={onChange}
                          onBlur={onBlur}
                          hasError={Boolean(error) && isTouched}
                          // Payment addresses are USD-only on the backend, so only offer USD accounts.
                          filters={{ currency_code: 'USD' }}
                        />
                      ),
                    },
                    {
                      id: 'chains',
                      label: 'Blockchain Network',
                      oneColumn: true,
                      description: 'Select the networks this payment address can send on.',
                      children: ({ value, onChange }) => {
                        const selected = (value as string[]) ?? [];
                        const toggleChain = (chain: string, checked: boolean) =>
                          onChange(checked ? [...selected, chain] : selected.filter((c) => c !== chain));

                        return (
                          <FieldGroup variant="outline" columns={2}>
                            {SUPPORTED_STABLECOIN_CHAINS.map((chain) => {
                              const checkboxId = `chain-${chain}`;
                              return (
                                <Field key={chain} orientation="horizontal">
                                  <Checkbox
                                    id={checkboxId}
                                    checked={selected.includes(chain)}
                                    onCheckedChange={(checked) => toggleChain(chain, checked === true)}
                                  />
                                  <FieldLabel
                                    htmlFor={checkboxId}
                                    className="text-muted-foreground peer-data-[state=checked]:text-foreground"
                                  >
                                    {STABLECOIN_CHAIN_LABELS[chain]}
                                    <ChainIcon chain={chain} className="ml-auto shrink-0 text-gray-400" />
                                  </FieldLabel>
                                </Field>
                              );
                            })}
                          </FieldGroup>
                        );
                      },
                    },
                  ],
                },
              ],
            })}
          </div>
        </FormProvider>

        <DialogFooter className="sm:justify-between">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handleCreate} disabled={isPending || !isValid}>
            <Icon.Plus style={{ width: 16, height: 16 }} />
            {isPending ? 'Creating…' : 'Create Payment Address'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
